import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveWorkspace, useSelectionStore } from '@/state/selection.store';
import { Heading } from '@/components/catalyst-ui/heading';
import { Button } from '@/components/catalyst-ui/button';
import { Text } from '@/components/catalyst-ui/text';
import DomainSearchSection from '@/features/domains/components/DomainSearchSection';
import MultiStepDomainRegistrationModal from '@/features/domains/components/MultiStepDomainRegistrationModal';
import { useSearchQuery, useDomainPrice } from '../hooks/useSearchQuery';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useAddUserDomain } from '../hooks/useMyDomains';
import { useAuth } from '@/features/auth/hooks/useAuth';
import GrowthPlanPromoWideHorizontal from '@/components/ui/GrowthPlanPromoWideHorizontal';
import { PlusIcon } from '@/assets/icons';
import { PinwheelLoader } from '@/components/ui/PinwheelLoader';
import { Pinwheel } from 'ldrs/react';
import 'ldrs/react/Pinwheel.css';
import { getDomainDiscount } from '../utils/priceCalculations';
import { hasFeature } from '@/lib/hasFeature';
import { useUIStore } from '@/state/ui.store';
import { useProjects } from '@/features/workspace/hooks/useProjects';
import ConnectProjectDropdown from '../components/ConnectProjectDropdown';
import { Dialog, DialogTitle, DialogDescription, DialogActions, DialogBody } from '@/components/catalyst-ui/dialog';
import { XMarkIcon } from '@heroicons/react/24/solid';

const CATEGORY_IDS = [
  'popular',
  'technology',
  'businesses',
  'real-estate',
  'finance',
  'arts-culture',
  'organizations',
  'shopping-sales',
  'cities',
  'sports-hobbies',
  'travel',
  'health-fitness',
  'environment',
  'social-lifestyle',
  'education',
  'international',
  'marketing',
  'products',
  'services',
  'media-music',
  'transport',
  'personal',
  'food-drink',
  'beauty',
  'colors',
  'trades-construction',
  'non-english',
];

const DOMAIN_PROMO_FEATURES = [
  { label: 'First year included*', muted: false },
  { label: '.com / .net / .info / .co', muted: false },
];

const DOMAIN_YEARLY_PLAN_FEATURE = 'domains_free_domain_yearly_plan';

function getActiveWorkspacePlans(workspace) {
  if (Array.isArray(workspace?.activePlans) && workspace.activePlans.length > 0) {
    return workspace.activePlans;
  }

  if (!Array.isArray(workspace?.plans)) {
    return [];
  }

  return workspace.plans.filter((plan) => plan?.isActive);
}

function isUsablePlan(plan) {
  const name = String(plan?.planName || '').toLowerCase();
  return Boolean(
    plan &&
    plan.isActive !== false &&
    plan.isCancelled !== true &&
    plan.hasRefunded !== true &&
    !name.includes('domain purchase'),
  );
}

function isYearlyWorkspacePlan(plan) {
  const values = [
    plan?.duration,
    plan?.planDuration,
    plan?.billingCycle,
    plan?.billingInterval,
    plan?.interval,
    plan?.planName,
    plan?.planType,
    plan?.name,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  return values.some((value) => (
    value === 'yearly' ||
    value === 'annual' ||
    value.includes('yearly') ||
    value.includes('annual')
  ));
}

export function hasYearlyWorkspacePlan(workspace) {
  return getActiveWorkspacePlans(workspace)
    .filter(isUsablePlan)
    .some(isYearlyWorkspacePlan);
}

const formatCategoryLabel = (label) => {
  if (typeof label !== 'string') {
    return 'Category';
  }
  const cleaned = label.replace(/-/g, ' ').trim();
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

export function DomainYearlyPlanPromo({ compact = false, onCheckout }) {
  if (compact) {
    return (
      <section className="w-full overflow-hidden rounded-2xl border border-[#F59E0B]/70 bg-[#FFFBEB] px-4 py-3 shadow-[0px_10px_30px_-22px_rgba(146,64,14,0.8)] transition-all duration-300 ease-out">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-[#B87700] bg-[#FFB020] px-2.5 py-1 text-[11px] font-semibold leading-4 text-[#09090B] shadow-[inset_0px_1.5px_0px_rgba(186,111,48,0.35)]">
                FREE DOMAIN
              </span>
              <span className="text-xs font-semibold uppercase leading-4 text-[#16A34A]">
                .com .net .info .co
              </span>
            </div>
            <p className="mt-1 text-sm font-semibold leading-5 text-[#18181B]">
              Get a free domain for the first year with a yearly plan*
            </p>
            <p className="text-xs leading-4 text-[#71717A]">
              Standard eligible domains only.
            </p>
          </div>

          <button
            type="button"
            onClick={onCheckout}
            className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xl bg-[#18181B] px-4 py-2 text-sm font-medium leading-5 text-white transition-colors hover:bg-[#27272A] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            View yearly plans
          </button>
        </div>
      </section>
    );
  }

  return (
    <GrowthPlanPromoWideHorizontal
      title="Get a free domain for one year with a yearly plan"
      badge="FREE DOMAIN"
      plan="GROWTH . YEARLY"
      price="$20"
      period="/mo."
      billedText="$240 billed yearly"
      savings="ELIGIBLE TLDS"
      features={DOMAIN_PROMO_FEATURES}
      cta="View yearly plans"
      securityText="*Terms apply. Standard domains only."
      leftPanelWidthClass="xl:w-[520px]"
      onCheckout={onCheckout}
    />
  );
}

// Component to handle individual domain with price fetching
const DomainItem = ({ domain, onGetItNow }) => {
  const [shouldFetchPrice, setShouldFetchPrice] = useState(false);

  // Only fetch price for available domains when component mounts
  useEffect(() => {
    if (domain.status === 'available') {
      // Add a small delay to stagger price requests
      const timer = setTimeout(() => {
        setShouldFetchPrice(true);
      }, Math.random() * 1000); // Random delay up to 1 second

      return () => clearTimeout(timer);
    }
  }, [domain.status]);

  const { data: priceData, isLoading: isPriceLoading, isError: isPriceError } = useDomainPrice(domain.domain, {
    enabled: shouldFetchPrice && domain.status === 'available',
  });

  const displayPrice = () => {
    if (domain.status !== 'available') return null;

    if (isPriceLoading) {
      return (
        <div className="flex items-center gap-2">
          <Pinwheel size={16} stroke={3.5} speed={0.9} color="#71717A" />
          <span className="text-sm text-gray-600">Loading price...</span>
        </div>
      );
    }

    if (isPriceError) {
      return <span className="text-sm text-red-500">Price unavailable</span>;
    }

    if (priceData && priceData.success) {
      const basePrice = priceData.price
      const prices = priceData.prices || {}

      const year1Price = parseFloat(prices['1']) || basePrice

      // Inflate the price to create a "was" strikethrough price
      // The backend price is the real price the user pays
      const discountPercent = getDomainDiscount(domain.domain)
      const inflatedPrice = year1Price / (1 - discountPercent / 100)

      return (
        <div className="text-base text-[#09090B] font-normal flex items-center gap-2">
          <span className="line-through text-[#09090BCC] text-base font-normal">${inflatedPrice.toFixed(2)}</span>
          <span className="font-medium text-gray-900">${year1Price.toFixed(2)}</span>
        </div>
      );
    }

    return <span className="text-sm text-gray-500">Price pending...</span>;
  };

  // const getStatusDisplay = () => {
  //   if (domain.status === 'available') {
  //     return (
  //       <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
  //         Available
  //       </span>
  //     );
  //   } else if (domain.status === 'taken') {
  //     return (
  //       <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
  //         Taken
  //       </span>
  //     );
  //   }
  //   return (
  //     <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
  //       {domain.status}
  //     </span>
  //   );
  // };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 bg-white border-b border-gray-200 hover:bg-gray-50 gap-3 sm:gap-0">
      <div className="flex items-center justify-between sm:flex-1 px-4 sm:px-4">
        <Text
          className='!font-normal !text-black'
        >
          {domain.domain}
        </Text>
        {domain.status === 'available' ? (
          <div className="sm:hidden">
            {displayPrice()}
          </div>
        ) : (
          <span className="text-sm text-gray-500">Unavailable</span>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-end sm:items-center justify-start sm:justify-end gap-3 px-4 sm:px-0">
        {domain.status === 'available' && (
          <>
            <div className="hidden sm:block">
              {displayPrice()}
            </div>
            <button
              type="button"
              onClick={() =>
                onGetItNow({
                  ...domain,
                  price: priceData?.price,
                  currency: priceData?.currency,
                  prices: priceData?.prices,
                })
              }
              className="inline-flex items-center px-3 py-2 whitespace-nowrap rounded-md bg-[#5C33FF] text-white text-sm font-semibold hover:opacity-90"
            >
              Get it now
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const DomainsPage = () => {
  const navigate = useNavigate();
  const { workspaceId, workspace } = useActiveWorkspace();
  const { user } = useAuth();
  const setShowPricingModal = useUIStore((state) => state.setShowPricingModal);
  const [searchQuery, setSearchQuery] = useState('');
  const [domains, setDomains] = useState([]);
  const [hasMore, setHasMore] = useState(false); // Start with false until we have search results
  const [activeCategory, setActiveCategory] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Add the mutation hook at component level
  const addDomainMutation = useAddUserDomain();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDomainTakenUI, setShowDomainTakenUI] = useState(false);
  const [takenDomain, setTakenDomain] = useState(null);

  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedDomainForRegistration, setSelectedDomainForRegistration] = useState(null);

  // Use the `useSearchQuery` mutation hook
  const searchMutation = useSearchQuery();
  const { projects } = useProjects(workspaceId);

  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showCreateProjectAlert, setShowCreateProjectAlert] = useState(false);

  // Handle the domain search
  const handleSearch = async (overrideCategory = null) => {
    if (searchQuery.trim()) {
      console.log('Starting search for:', searchQuery.trim());
      // Allow event handlers to call handleSearch directly
      if (overrideCategory && typeof overrideCategory === 'object' && 'preventDefault' in overrideCategory) {
        overrideCategory.preventDefault();
        overrideCategory = null;
      }
      let categoryId = overrideCategory;
      if (!categoryId && activeCategory) {
        categoryId = activeCategory;
      }
      setDomains([]); // Clear previous results
      setCurrentPage(1); // Reset to the first page
      setHasMore(false); // Reset until we get results
      setShowDomainTakenUI(false); // Reset domain taken UI
      setTakenDomain(null);
      setMetadata(null);

      try {
        const payload = {
          searchQuery: searchQuery.trim(),
          page: 1,
          limit: 20,
        };

        if (categoryId) {
          payload.category = categoryId;
        }

        const result = await searchMutation.mutateAsync({
          ...payload
        });
        console.log('Search result:', result);

        if (result && result.data) {
          // Check if this is a domain search with a dot (like "example.com")
          const hasDot = searchQuery.trim().includes('.');

          if (hasDot) {
            // TEMPORARY: Always show "I own this domain" for testing
            const exactDomain = searchQuery.trim();
            // Original logic checked for status === 'taken':
            // const takenDomainResult = result.data.find(domain => 
            //   domain.domain === exactDomain && domain.status === 'taken'
            // );

            // TEMP: Always show the option for any domain with a dot
            setShowDomainTakenUI(true);
            setTakenDomain(exactDomain);
            setDomains(result.data);
            // Don't return early so domains still show
          } else {
            // If no dot, ensure domain taken UI is hidden
            setShowDomainTakenUI(false);
            setTakenDomain(null);
          }

          setDomains(result.data); // Set the domains from result.data
          setCurrentPage(1);

          // Update hasMore based on pagination
          if (result.pagination) {
            setHasMore(result.pagination.hasNextPage);
            console.log('Pagination info:', result.pagination);
          }

          setActiveCategory(categoryId || null);
          setMetadata(result.metadata || null);
        }
      } catch (error) {
        console.error('Error during search:', error);
      }
    }
  };

  // Function to load more domains for infinite scroll
  const loadMoreDomains = async () => {
    if (!hasMore || searchMutation.isPending) return;

    setIsLoadingMore(true);

    try {
      // Call the API with the next page
      const payload = {
        searchQuery: searchQuery.trim(),
        page: currentPage + 1,
        limit: 20,
      };

      if (activeCategory) {
        payload.category = activeCategory;
      }

      const result = await searchMutation.mutateAsync(payload);
      console.log('Load more result:', result);

      if (result && result.data) {
        // Append new domains to existing ones
        setDomains(prevDomains => [...prevDomains, ...result.data]);
        setCurrentPage(currentPage + 1);

        // Update hasMore based on pagination
        if (result.pagination) {
          setHasMore(result.pagination.hasNextPage);
        }
      }
    } catch (error) {
      console.error('Error loading more domains:', error);
    }

    setIsLoadingMore(false);
  };

  const handleGetItNow = (domainData) => {
    // Add domain to selected domains for registration
    setSelectedDomainForRegistration(domainData);
    setShowRegistrationModal(true);
  };

  const handleOwnDomain = () => {
    if (!takenDomain) return;

    // Ensure we have a valid workspace ID
    if (!workspaceId) {
      console.error('No workspace ID available');
      return;
    }

    if (!projects || projects.length === 0) {
      setShowCreateProjectAlert(true);
      return;
    }

    setShowProjectDialog(true);
  };

  const handleProjectSelected = (project) => {
    if (project?.order?.paid) {
      proceedWithDomainAdd(project)
    } else {
      // Set the project in the store so hasFeature/pricing modal can see it
      const normalizedProject = { ...project, id: project.id || project._id }
      useSelectionStore.getState().setCurrentProject(normalizedProject)
      hasFeature('DOMAIN_CONNECTION', () => {
        // License was assigned — proceed
        proceedWithDomainAdd(project)
      }, ['limitPremium'])
    }
  };

  const proceedWithDomainAdd = async (project) => {
    try {
      const result = await addDomainMutation.mutateAsync({
        userId: user?.id,
        name: takenDomain,
        project: project.uniqueUrl || project.name,
        workspaceId: workspaceId,
      });
      navigate(`/domains/my-domain/records?workspace=${encodeURIComponent(workspaceId)}&domain=${encodeURIComponent(takenDomain)}`, { state: { apiData: result.data } });
    } catch (error) {
      console.error('Error creating domain:', error);
      navigate(`/domains/my-domain/records?workspace=${encodeURIComponent(workspaceId)}&domain=${encodeURIComponent(takenDomain)}`);
    }
  };

  const handleSearchInputChange = (value) => {
    setSearchQuery(value);
    // Reset domain taken UI when user starts typing
    if (showDomainTakenUI) {
      setShowDomainTakenUI(false);
      setTakenDomain(null);
    }
    setActiveCategory(null);
  };

  const handleCategorySelect = async (categoryId) => {
    setActiveCategory(categoryId);
    await handleSearch(categoryId);
  };

  const handleToggleCategories = () => {
    setShowCategories(prev => !prev);
  };

  const handleDomainPromoCheckout = () => {
    setShowPricingModal(true, DOMAIN_YEARLY_PLAN_FEATURE);
  };

  const categoryList = useMemo(() => CATEGORY_IDS, []);
  const showCompactDomainPromo = searchMutation.isPending || domains.length > 0;
  const showDomainPromo = !hasYearlyWorkspacePlan(workspace);

  return (
    <div className="max-w-[896px] flex flex-col mx-auto">
      <DomainSearchSection
        domainInput={searchQuery}
        onDomainInputChange={handleSearchInputChange}
        onDomainSearch={handleSearch}
      />

      {showDomainPromo && (
        <div className="mb-8 mt-8 flex w-full justify-center">
          <DomainYearlyPlanPromo
            compact={showCompactDomainPromo}
            onCheckout={handleDomainPromoCheckout}
          />
        </div>
      )}

      {/* Show loading state */}
      {searchMutation.isPending && domains.length === 0 && (
        <div className="text-center py-8">
          <PinwheelLoader text="Checking domain availability..." />
        </div>
      )}

      {/* Show error state */}
      {searchMutation.isError && (
        <div className="text-center py-8">
          <Text className="text-red-600 mb-4">Error checking domain availability. Please try again.</Text>
          <Button onClick={handleSearch} outline className="data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2">Try Again</Button>
        </div>
      )}

      {/* Show domain taken UI */}
      {showDomainTakenUI && takenDomain && (
        <div className="text-center">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 p-4">
            <Text className="!text-[#71717B] !text-sm !font-semibold whitespace-nowrap">
              {takenDomain} is taken. Is it yours?
            </Text>
            <div className="flex justify-center">
              <Button
                onClick={handleOwnDomain}
                color="white"
                className="px-6 py-2 border whitespace-nowrap border-gray-300 !font-normal rounded-md text-black hover:bg-gray-50"
              >
                Yep, I own this domain
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Show results if available */}
      {domains.length > 0 && (
        <div className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Heading level={3} className="text-lg font-semibold">
                {activeCategory ? `${formatCategoryLabel(activeCategory)} domains` : 'Generic TLD suggestions'}
              </Heading>
              {metadata?.tlds_used ? (
                <Text className="text-sm text-gray-500">
                  {/* //  Using TLDs: {metadata.tlds_used.map((tld) => `.${tld}`).join(', ')} */}
                </Text>
              ) : (
                <Text className="text-sm text-gray-500">
                  Showing {domains.length} suggestion{domains.length === 1 ? '' : 's'}
                </Text>
              )}
            </div>
            <Button
              onClick={handleToggleCategories}
              color="light"
              className="flex items-center gap-2 border border-gray-300"
            >
              <PlusIcon className="h-4 w-4" />
              {showCategories ? 'Explore less' : 'More'}
            </Button>
          </div>

          {showCategories && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap gap-2">
                {categoryList.map((categoryId) => {
                  const isActive = activeCategory === categoryId || (!activeCategory && categoryId === 'popular');
                  return (
                    <button
                      key={categoryId}
                      type="button"
                      className={`rounded-full border px-3 py-1.5 text-sm transition ${isActive ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-gray-50 text-gray-700'
                        }`}
                      onClick={() => handleCategorySelect(categoryId)}
                    >
                      {formatCategoryLabel(categoryId)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <InfiniteScroll
            dataLength={domains.length}
            next={loadMoreDomains}
            hasMore={hasMore}
            loader={
              <div className="text-center py-4">
                <PinwheelLoader text="Loading more domains..." />
              </div>
            }
            // endMessage={
            //   <div className="text-center py-4">
            //     <Text className="text-gray-500">No more domains to load</Text>
            //   </div>
            // }
            scrollThreshold={0.9}
          >
            <div className="space-y-">
              {domains.map((domain, index) => (
                <DomainItem key={`${domain.domain}-${index}`} domain={domain} onGetItNow={handleGetItNow} />
              ))}
            </div>
          </InfiniteScroll>

          {isLoadingMore && (
            <div className="text-center text-sm text-gray-500">Fetching more domains...</div>
          )}
        </div>
      )}

      {/* Show no results message */}
      {/* {domains.length === 0 && !searchMutation.isPending && searchQuery && (
        <div className="text-center py-8">
          <Text className="text-gray-600">No domains found for "{searchQuery}". Try a different search term.</Text>
        </div>
      )} */}

      {/* Domain Registration Modal */}
      <MultiStepDomainRegistrationModal
        open={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        domainData={selectedDomainForRegistration}
      />

      <ConnectProjectDropdown
        open={showProjectDialog}
        onOpenChange={setShowProjectDialog}
        projects={projects || []}
        onSelect={handleProjectSelected}
        buttonText={null}
      />

      <Dialog
        open={showCreateProjectAlert}
        onClose={() => setShowCreateProjectAlert(false)}
        className="!rounded-xl !p-5 max-w-[512px]"
      >
        <div className="flex items-start justify-between gap-3">
          <DialogTitle className="!text-lg !font-semibold !text-[#09090B]">
            Create a project first
          </DialogTitle>
          <button
            type="button"
            onClick={() => setShowCreateProjectAlert(false)}
            aria-label="Close"
            className="-mt-2.5 -mr-2.5 rounded-md p-1 cursor-pointer hover:text-[#09090B] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <DialogBody className="!mt-2">
          <DialogDescription className="!text-sm !font-medium !text-[#71717A]">
            You need to have at least one project to add a domain. Please create a project in your workspace first.
          </DialogDescription>
        </DialogBody>
        <DialogActions className="!mt-4">
          <Button outline onClick={() => setShowCreateProjectAlert(false)} className="!w-full">
            Close
          </Button>
          <Button color="dark" onClick={() => navigate(`/overview?workspace=${workspaceId}&createProject=true`)} className="!w-full">
            Create Project
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DomainsPage;
